import React, { useMemo, useState } from 'react';
import type { CentralDailyPoint } from '../../services/MendsVisionService';
import { formatDay } from '../../lib/centralFormat';

type SeriesKey = 'inbound' | 'aiMessages' | 'humanMessages';

/** Ordem fixa das cores (paleta categórica validada: azul, laranja, água). A cor segue a série, nunca o rank. */
const SERIES: Array<{ key: SeriesKey; label: string; color: string }> = [
  { key: 'inbound', label: 'Clientes escreveram', color: '#2a78d6' },
  { key: 'aiMessages', label: 'IA respondeu', color: '#eb6834' },
  { key: 'humanMessages', label: 'Equipe respondeu', color: '#1baf7a' },
];

const W = 640;
const H = 200;
const PAD = { top: 12, right: 12, bottom: 26, left: 36 };

function niceMax(v: number): number {
  if (v <= 5) return 5;
  const pow = 10 ** Math.floor(Math.log10(v));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

/** Mensagens por dia (um eixo só). Reuniões ficam fora: escala diferente, vão no texto ao lado. */
export const CentralTrendChart: React.FC<{ points: CentralDailyPoint[] }> = ({ points }) => {
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);

  const { max, x, y } = useMemo(() => {
    const top = niceMax(Math.max(1, ...points.flatMap((p) => SERIES.map((s) => p[s.key]))));
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;
    const step = points.length > 1 ? innerW / (points.length - 1) : 0;
    return {
      max: top,
      x: (i: number) => PAD.left + (points.length > 1 ? i * step : innerW / 2),
      y: (v: number) => PAD.top + innerH - (v / top) * innerH,
    };
  }, [points]);

  const meetings = points.reduce((s, p) => s + p.meetingsBooked, 0);
  const total = points.reduce((s, p) => s + p.inbound + p.aiMessages + p.humanMessages, 0);

  if (points.length === 0 || total === 0) {
    return <p className="mai-ci-empty">Nenhuma mensagem trocada no período.</p>;
  }

  const labelEvery = Math.max(1, Math.ceil(points.length / 8));
  const hp = hover !== null ? points[hover] : null;

  return (
    <div className="mai-ci-trend">
      <div className="mai-ci-legend" aria-hidden={table}>
        {SERIES.map((s) => (
          <span key={s.key} className="mai-ci-legend-item">
            <i style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
        <button type="button" className="mai-ci-link" onClick={() => setTable((t) => !t)}>
          {table ? 'Ver gráfico' : 'Ver tabela'}
        </button>
      </div>
      {table ? (
        <div className="mai-ci-table-wrap">
          <table className="mai-ci-table">
            <thead>
              <tr>
                <th>Dia</th>
                {SERIES.map((s) => <th key={s.key}>{s.label}</th>)}
                <th>Reuniões marcadas pela IA</th>
              </tr>
            </thead>
            <tbody>
              {points.map((p) => (
                <tr key={p.date}>
                  <td>{formatDay(p.date)}</td>
                  {SERIES.map((s) => <td key={s.key}>{p[s.key]}</td>)}
                  <td>{p.meetingsBooked}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mai-ci-chart" onMouseLeave={() => setHover(null)}>
          <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Mensagens por dia no período">
            {[0, 0.5, 1].map((f) => (
              <g key={f}>
                <line x1={PAD.left} x2={W - PAD.right} y1={y(max * f)} y2={y(max * f)} className="mai-ci-grid" />
                <text x={PAD.left - 6} y={y(max * f) + 4} className="mai-ci-axis" textAnchor="end">{Math.round(max * f)}</text>
              </g>
            ))}
            {points.map((p, i) => (i % labelEvery === 0 || i === points.length - 1) && (
              <text key={p.date} x={x(i)} y={H - 6} className="mai-ci-axis" textAnchor="middle">{formatDay(p.date)}</text>
            ))}
            {hover !== null && (
              <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={H - PAD.bottom} className="mai-ci-crosshair" />
            )}
            {SERIES.map((s) => (
              <g key={s.key}>
                <polyline
                  fill="none"
                  stroke={s.color}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  points={points.map((p, i) => `${x(i)},${y(p[s.key])}`).join(' ')}
                />
                {hover !== null && (
                  <circle cx={x(hover)} cy={y(points[hover][s.key])} r={4} fill={s.color} stroke="#fff" strokeWidth={2} />
                )}
              </g>
            ))}
            {points.map((p, i) => {
              const half = points.length > 1 ? (x(1) - x(0)) / 2 : W / 2;
              return (
                <rect
                  key={p.date}
                  x={x(i) - half}
                  y={PAD.top}
                  width={half * 2}
                  height={H - PAD.top - PAD.bottom}
                  fill="transparent"
                  onMouseEnter={() => setHover(i)}
                  onTouchStart={() => setHover(i)}
                />
              );
            })}
          </svg>
          {hp && (
            <div
              className="mai-ci-tooltip"
              style={{ left: `${(x(hover!) / W) * 100}%` }}
              role="status"
            >
              <strong>{formatDay(hp.date)}</strong>
              {SERIES.map((s) => (
                <span key={s.key}><i style={{ background: s.color }} />{s.label}: {hp[s.key]}</span>
              ))}
              <span>Reuniões marcadas pela IA: {hp.meetingsBooked}</span>
            </div>
          )}
        </div>
      )}
      <p className="mai-ci-note">
        {meetings === 0
          ? 'Nenhuma reunião marcada pela IA no período.'
          : `${meetings} reuni${meetings === 1 ? 'ão marcada' : 'ões marcadas'} pela IA no período (toque ou passe o mouse no gráfico para ver por dia, ou abra a tabela).`}
      </p>
    </div>
  );
};
